import React from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import InputMask from "react-input-mask";
import NotificationCard from "../../notificationCard/NotificationCard";
import Notification from "../../../../../interfaces/Notification";
import Select from "../../../../../../../shared/select/Select";
import SelectItem from "../../../../../../../shared/select/SelectItem";
import { Input } from "../../../../../../../shared/native/input.styles";
import documentsInfo from "../../../constants/documentsInfo";
import noUnderlines from "../../../../../../../../core/regexp/noUnderlines";
import colors from "../../../../../../../../core/styles/colors";
import { useAppDispatch } from "../../../../../../../../core/hooks/redux";
import { submitNotificationRequest } from "../../../../../../../../http/requests/notifications.http";
import { removeNotification } from "../../../../../../../../store/modules/notifications/notificationsSlice";
import getFormComponentByAnswer from "./constants/getFormComponentByAnswer";
import defaultFormValues from "./constants/defaultFormValues";
import NotificationSubmitForm from "../../../../../interfaces/NotificationSubmitForm";
import convertFormData from "./utils/convertFormData";
import degreeType from "../../../../../../../../core/types/degreeType";
import Checkbox from "../../../../../../../shared/checkbox/Checkbox";
import { EduCreditInfo } from "./formType.styles";
import AddFileButton from "../../../../../../../shared/addFileButton/AddFileButton";
import AddFileInfo from "../../../../../../../shared/addFileInfo/AddFileInfo";
import { FieldData } from "../../../../../../../shared/form/fieldData.styles";

const FormType: React.FC<{ notification: Notification; degree: degreeType }> = ({
  notification,
  degree,
}) => {
  const dispatch = useAppDispatch();
  const [disableResponseButton, setDisableResponseButton] = React.useState(false);
  const { handleSubmit, control, register, setValue, formState } = useForm<
    NotificationSubmitForm & { answer: number; files: File[]; notice: string }
  >({
    defaultValues: { ...defaultFormValues, files: [] },
  });
  const answerWatch = useWatch({ control, name: "answer" });

  const submitHandler = (
    data: NotificationSubmitForm & { answer: number; files?: File[]; notice: string }
  ): void => {
    setDisableResponseButton(true);

    submitNotificationRequest({
      notify_id: notification.uid,
      type: +notification.document_type,
      files: data.files,
      answer: data.answer ?? null,
      notice: data.notice,
      form: notification.document_type === "11" ? null : convertFormData(data),
    }).then((res) => {
      setDisableResponseButton(false);
      if (res.data.error) {
        return;
      }

      dispatch(removeNotification(notification.uid));
    });
  };

  const cancelHandler = (): void => {
    setDisableResponseButton(true);

    submitNotificationRequest({
      notify_id: notification.uid,
      type: +notification.document_type,
      delete_notification: 1,
    }).then((res) => {
      setDisableResponseButton(false);
      if (res.data.error) {
        return;
      }

      dispatch(removeNotification(notification.uid));
    });
  };

  const isPersonalDataChangeNotification = notification.document_type === "11";

  return (
    <NotificationCard
      title={notification?.document_name}
      comment={notification.comment}
      handleSubmit={handleSubmit(submitHandler)}
      disableResponseButton={disableResponseButton}
      outlinedResponseButton={isPersonalDataChangeNotification}
      showCancelButton={isPersonalDataChangeNotification}
      onCancel={cancelHandler}
    >
      {notification?.dict ? (
        <>
          <Select
            items={notification.dict.map(
              (dict) =>
                ({
                  label: dict.name,
                  value: dict.id,
                } as SelectItem)
            )}
            control={control}
            options={{ required: "Обязательное поле" }}
            name="answer"
            placeholder="Выберите ответ"
            whiteBackground
          />

          {notification.document_type === "3" && (
            <>
              <Checkbox
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                control={control}
                style={{ marginTop: 8 }}
                label="Образовательный кредит"
                name="eduCredit"
              />

              <EduCreditInfo>
                При оплате образовательным кредитом необходимо выбрать способ оплаты —{" "}
                <span>по семестрам</span> или <span>за год</span>. Плательщик по договору –{" "}
                <span>«Оплачиваю сам»</span>. Если поступающему <span>менее 18 лет</span>, то
                необходимо выбрать плательщиком - <span>«Физическое лицо»</span>
              </EduCreditInfo>
            </>
          )}
          {getFormComponentByAnswer(degree)[answerWatch]?.element?.({
            control,
            register,
            setValue,
            formState,
          })}
        </>
      ) : (
        <>
          {documentsInfo[notification.document_id] ? (
            <Controller
              control={control}
              name="answer"
              rules={{
                required: "Обязательное поле",
                pattern: {
                  value: noUnderlines,
                  message: "Неполное значение",
                },
                validate: (value) =>
                  documentsInfo[notification.document_id]?.validate
                    ? documentsInfo[notification.document_id]?.validate(value)
                    : true,
              }}
              render={({ field: { value, onChange } }): React.ReactElement => (
                <InputMask
                  mask={documentsInfo[notification.document_id].mask}
                  value={value}
                  onChange={onChange}
                >
                  <Input
                    placeholder={
                      documentsInfo[notification.document_id].placeholder ?? "Введите ответ"
                    }
                    hasErrors={!!formState?.errors?.answer}
                    style={{ background: colors.white.general }}
                  />
                </InputMask>
              )}
            />
          ) : (
            <>
              <Input
                placeholder="Введите ответ"
                {...register("notice", { required: "Обязательное поле" })}
                hasErrors={!!formState?.errors?.notice}
                style={{ background: colors.white.general }}
              />
              {notification.document_type === "11" && (
                <>
                  <FieldData>
                    <AddFileButton
                      style={{ marginTop: 8 }}
                      control={control}
                      setValue={setValue}
                      name="files"
                      options={{ required: false }}
                      accept="image/*"
                      multiple
                      previewProps={{
                        whiteBackground: true,
                      }}
                      buttonProps={{
                        whiteBackground: true,
                      }}
                    >
                      Загрузить
                    </AddFileButton>
                  </FieldData>
                  <FieldData>
                    <AddFileInfo />
                  </FieldData>
                </>
              )}
            </>
          )}
        </>
      )}
    </NotificationCard>
  );
};

export default FormType;
